import type { TaskCaseRow } from '../data/tasks';
import type { TaskListScope } from '../data/taskStatus';
import { uniqueColumnSuggestions } from '../utils/columnFilter';
import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import { adaptApiTaskRow, type ApiTaskRow } from './taskListAdapter';
import type { ApiResult } from './types';
import { isCaseRequestUuid } from '../pages/task-case/taskCaseUpdateHelpers';

export type TaskListResult = {
  items: TaskCaseRow[];
  total: number;
  totalPages: number;
};

export type TaskListParams = {
  scope: TaskListScope;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  columnFilters?: Record<string, string>;
  signal?: AbortSignal;
};

type ApiTaskPage = {
  content?: ApiTaskRow[];
  totalElements?: number;
  totalPages?: number;
};

/** DataTable column key → CMS-CRC-18/20/21 request body filter field (camelCase). */
export const FILTER_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  stage: 'currentWorkflowStage',
  status: 'taskStatus',
  urgency: 'urgencyLevel',
  officer: 'assignedOfficer',
};

/** DataTable column key → CMS-CRC-18/20/21 sortBy value. */
export const SORT_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  submittedAt: 'submittedAt',
  stage: 'currentWorkflowStage',
  status: 'taskStatus',
  time: 'timeRemainingDisplay',
  urgency: 'urgencyLevel',
  officer: 'assignedOfficer',
};

/** CMS-CRC-18 / CMS-CRC-20 / CMS-CRC-21 / CMS-CRC-39 / CMS-CRC-40 — task list endpoints by scope. */
const SCOPE_ENDPOINT: Record<TaskListScope, string> = {
  'active-mine': '/my-active-task-case-requests',
  'active-linked': '/my-linked-active-task-list',
  'active-created': '/my-linked-active-task-list-created-by-officer',
  completed: '/my-completed-task-list',
  'ma-active': '/my-management-assistant-active-tasks',
  'ma-completed': '/my-management-assistant-completed-tasks',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Page size for tab-badge count requests — large enough to detect under-reported totals. */
export const TASK_TAB_COUNT_PAGE_SIZE = 200;

/** Reconcile API totalElements with returned rows (linked list can under-report). */
export function resolveTaskListTotal(totalElements: number | undefined, contentLength: number): number {
  const reported = typeof totalElements === 'number' && totalElements >= 0 ? totalElements : contentLength;
  return Math.max(reported, contentLength);
}

/** CMS-CRC-18/20/21 — date picker value (`YYYY-MM-DD`) → `submitted_at` filter. */
export function submittedAtColumnFilterToIso(value: string): string {
  const date = value.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
  return `${date}T00:00:00.000Z`;
}

async function fetchTaskList(params: TaskListParams): Promise<TaskListResult> {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const endpoint = SCOPE_ENDPOINT[params.scope];
  const qs = new URLSearchParams({
    pageNumber: String(page),
    pageSize: String(pageSize),
  });

  const res = await caseApiRequest<ApiTaskPage>(`${endpoint}?${qs.toString()}`, {
    method: 'POST',
    body: buildListBody(params),
    signal: params.signal,
  });

  if (!res.ok) {
    if (res.aborted || params.signal?.aborted) {
      const err = new Error('Aborted');
      err.name = 'AbortError';
      throw err;
    }
    throw new Error(res.message);
  }

  const content = res.data.content ?? [];
  const total = resolveTaskListTotal(res.data.totalElements, content.length);
  const totalPages =
    typeof res.data.totalPages === 'number' && res.data.totalPages > 0
      ? res.data.totalPages
      : total === 0
        ? 1
        : Math.max(1, Math.ceil(total / pageSize));

  return {
    items: content.map((r) => adaptApiTaskRow(r, params.scope)),
    total,
    totalPages,
  };
}

export function buildListBody(params: TaskListParams): Record<string, string> {
  const body: Record<string, string> = {
    sortBy: SORT_COL_MAP[params.sortBy ?? 'ref'] ?? 'fileRefNo',
    sortDirection: params.sortDir ?? 'ASC',
  };
  if (params.columnFilters) {
    for (const [colId, value] of Object.entries(params.columnFilters)) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (colId === 'submittedAt') {
        const iso = submittedAtColumnFilterToIso(trimmed);
        if (iso) body.submitted_at = iso;
        continue;
      }
      const apiField = FILTER_COL_MAP[colId];
      if (apiField) body[apiField] = trimmed;
    }
  }
  return body;
}

/**
 * CMS intranet — paginated tasks & cases list.
 * All scopes call live CMS-CRC-18/20/21 APIs (never hardcoded rows).
 */
export async function listTasks(params: TaskListParams): Promise<TaskListResult> {
  return fetchTaskList(params);
}

/** Tab badge counts — lightweight totals per scope. */
export async function fetchTaskTabCounts(signal?: AbortSignal): Promise<{
  activeMine: number;
  activeLinked: number;
  activeCreated: number;
  completed: number;
}> {
  const scopes = ['active-mine', 'active-linked', 'active-created', 'completed'] as const;
  const results = await Promise.allSettled(
    scopes.map((scope) => listTasks({ scope, page: 0, pageSize: TASK_TAB_COUNT_PAGE_SIZE, signal })),
  );

  const totalFor = (index: number): number => {
    const r = results[index];
    return r?.status === 'fulfilled' ? r.value.total : 0;
  };

  return {
    activeMine: totalFor(0),
    activeLinked: totalFor(1),
    activeCreated: totalFor(2),
    completed: totalFor(3),
  };
}

export async function fetchTaskColumnSuggestions(
  scope: TaskListScope,
  columnKey: string,
  prefix: string,
): Promise<string[]> {
  const col = FILTER_COL_MAP[columnKey] ? columnKey : 'ref';
  const result = await listTasks({ scope, page: 0, pageSize: 200, columnFilters: prefix ? { [col]: prefix } : {} });
  const getter = (r: TaskCaseRow): string => {
    switch (col) {
      case 'fileCategory':
        return r.fileCategory;
      case 'subFileCategory':
        return r.subFileCategory;
      case 'agency':
        return r.agency;
      case 'stage':
        return r.stage;
      case 'status':
        return r.status;
      case 'urgency':
        return r.urgencyLevel;
      case 'officer':
        return r.assignedOfficer ?? '';
      default:
        return r.ref;
    }
  };
  return uniqueColumnSuggestions(result.items.map(getter), prefix);
}

/** Mark task notification read / unread (API TBD). */
export async function toggleTaskNotification(taskId: string, markRead: boolean): Promise<boolean> {
  if (!UUID_RE.test(taskId)) return false;
  const res = await caseApiRequest<unknown>(`/tasks/${encodeURIComponent(taskId)}/notification`, {
    method: 'POST',
    body: { read: markRead },
  });
  return res.ok;
}

/** Delete intranet draft (CMS-CRC-11 pattern). */
export async function deleteTaskDraft(caseRequestId: string): Promise<boolean> {
  if (!UUID_RE.test(caseRequestId)) return false;
  const res = await caseApiRequest<string>(`/${encodeURIComponent(caseRequestId)}`, { method: 'DELETE' });
  return res.ok;
}

/** Duplicate case request (CMS-CRC-12). */
export async function duplicateTaskCase(caseRequestId: string): Promise<string | null> {
  if (!UUID_RE.test(caseRequestId)) return null;
  const res = await caseApiRequest<{ duplicatedCaseRequestId?: string }>(
    `/${encodeURIComponent(caseRequestId)}/duplicate`,
    { method: 'POST' },
  );
  if (res.ok && res.data.duplicatedCaseRequestId) return res.data.duplicatedCaseRequestId;
  return null;
}

/** Transfer criminal advice case request to litigation draft (CMS-CRC-34). */
export async function transferTaskCase(caseRequestId: string): Promise<string | null> {
  if (!UUID_RE.test(caseRequestId)) return null;
  const res = await caseApiRequest<{ duplicatedCaseRequestId?: string }>(
    `/${encodeURIComponent(caseRequestId)}/transfer-case`,
    { method: 'POST' },
  );
  if (res.ok && res.data.duplicatedCaseRequestId) return res.data.duplicatedCaseRequestId;
  return null;
}

/** Initiate physical file retrieval from Record Room (data dictionary — Retrieve Physical File). */
export async function requestPhysicalFileRetrieval(caseRequestId: string): Promise<{ success: boolean; message?: string }> {
  if (!UUID_RE.test(caseRequestId)) {
    return { success: false, message: 'Invalid case request.' };
  }
  const res = await caseApiRequest<unknown>(`/physical-file/${encodeURIComponent(caseRequestId)}/retrieval-request`, {
    method: 'POST',
  });
  if (res.ok) return { success: true };
  return { success: false, message: res.message };
}

/** CMS-CRC-26 — Start a workflow user task and set stage started_at */
export async function startUserTask(input: {
  caseRequestId: string;
  workflowInstanceStageUserTaskId: string;
  applicationCode?: string;
}): Promise<ApiResult<void>> {
  const caseRequestId = input.caseRequestId.trim();
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();
  if (!isCaseRequestUuid(caseRequestId)) return { success: false, message: 'Invalid case request id.' };
  if (!isCaseRequestUuid(workflowInstanceStageUserTaskId)) return { success: false, message: 'Invalid user task id.' };

  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_START_USER_TASK ?? 'INTRANET').trim();

  const res = await caseApiRequest<{ status?: string; message?: string } | unknown>('/start-user-task', {
    method: 'POST',
    headers: { applicationCode, 'application-code': applicationCode },
    body: { caseRequestId, workflowInstanceStageUserTaskId },
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) return { success: false, message: dataPayloadErrorMessage(res.data) };
  return { success: true, data: undefined };
}
